/**
 * @fileoverview Per-branch and per-region financial-year lookup for the banking feature.
 * @description Ported from the InsightBox Banking design system's own `data/branchFinancials.ts`,
 * including its `regionCoreYear` (region-wide aggregate) — backs the Bank Regional Manager role's
 * "All <Region> Branches" figures. CRAR is intentionally excluded from both branch- and
 * region-level aggregation — capital adequacy is a bank-level regulatory ratio, not something
 * meaningfully attributable to a branch or region (see `banking.types.ts`'s `meta.notes.crar`).
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { branchesInRegion } from '@/features/banking/constants/banking-options.constants'

import type { Branch, FiscalYear } from '@/features/banking/constants/banking-options.constants'
import type { BankDataPayload, CoreYear } from '@/features/banking/types/banking.types'

/**
 * @description Real per-branch figures for a fiscal year — the branch-level counterpart to the
 * bank-wide `core.all[fy]`. Falls back to the bank-wide figure if the branch id isn't in the
 * fetched dataset (shouldn't happen in practice).
 * @param {BankDataPayload} data - The full fetched banking dataset.
 * @param {FiscalYear} fy - The fiscal year to look up.
 * @param {string} branchId - The branch id to look up.
 * @returns {CoreYear} That branch's figures for `fy`.
 */
export function branchCoreYear(data: BankDataPayload, fy: FiscalYear, branchId: string): CoreYear {
  return data.core.branches[branchId]?.[fy] ?? data.core.all[fy]
}

/**
 * @description Region-wide figures for a fiscal year, aggregated across every real branch in that
 * region — the region-level counterpart to `branchCoreYear`. Every additive field (₹ Cr amounts,
 * headcounts) is summed; `npaPct` (a ratio, not additive — see `meta.notes.npaPct`) is
 * advances-weighted across the region's branches, the same rule already applied at branch scope;
 * `crar` stays the bank-wide figure regardless, since it's never attributable below the whole bank.
 * @param {BankDataPayload} data - The full fetched banking dataset.
 * @param {FiscalYear} fy - The fiscal year to look up.
 * @param {Branch['location']} location - The region to aggregate.
 * @returns {CoreYear} That region's figures for `fy`.
 */
export function regionCoreYear(
  data: BankDataPayload,
  fy: FiscalYear,
  location: Branch['location'],
): CoreYear {
  const rows = branchesInRegion(location).map((branch) => branchCoreYear(data, fy, branch.id))
  const sum = (selector: (row: CoreYear) => number) =>
    rows.reduce((total, row) => total + selector(row), 0)
  const totalAdvances = sum((row) => row.advances)
  const weightedNpaPct =
    totalAdvances > 0
      ? rows.reduce((total, row) => total + row.npaPct * row.advances, 0) / totalAdvances
      : 0

  return {
    shareCapital: sum((row) => row.shareCapital),
    reserves: sum((row) => row.reserves),
    deposits: sum((row) => row.deposits),
    advances: totalAdvances,
    investments: sum((row) => row.investments),
    netProfit: sum((row) => row.netProfit),
    crar: data.core.all[fy].crar,
    npaPct: weightedNpaPct,
    employees: sum((row) => row.employees),
    totalIncome: sum((row) => row.totalIncome),
    totalExpenses: sum((row) => row.totalExpenses),
    totalMembers: sum((row) => row.totalMembers),
    totalCustomers: sum((row) => row.totalCustomers),
    newMembers: sum((row) => row.newMembers),
    cancelledMembers: sum((row) => row.cancelledMembers),
  }
}
